import React, { useState, useContext, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { LoginIcon } from "../../assets/svg/LoginIcon";
import { TitleText } from "../../ui/Text";
import { Formik, Field } from "formik";
import Input from "../../ui/Input";
import { SubmitButton } from "../../ui/SubmitButton";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import Axios from "axios";
import { LOGIN_URL } from "../../api/urls";
import { get } from "../../utils/get";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { Visibility } from "@material-ui/icons";
import { VisibilityOff } from "@material-ui/icons";
import useAuth from "../../hooks/useAuth";

const LoginSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

export const Login = () => {
  const authContext = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const history = useHistory();
  const [isMounted, setIsMounted] = useState(false);
  const [loginError, setLoginError] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsMounted(true);
    }, 100);
  }, []);

  const handleLogin = (values) => {
    setLoginError(false);
    Axios.post(`${LOGIN_URL}`, values)
      .then((res) => {
        toast.dismiss();
        const { access_token, refresh_token, user } = get(res, "data");
        const authInfo = {
          access_token,
          refresh_token,
          user,
        };
        console.log('User Data: ', authInfo);
        const { company_id } = user;
        authContext.setAuthState(authInfo);
        toast.success("Log in successful");
        history.push(`/${company_id}/dashboard`);
      })
      .catch((error) => {
        setLoginError(true);
        toast.error(get(error, "response.data.message"));
      });
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Container>
      <LoginContainer isMounted={isMounted}>
        <Formik
          initialValues={{ username: "", password: "" }}
          onSubmit={(values) => handleLogin(values)}
          validationSchema={LoginSchema}
        >
          {({ handleSubmit }) => (
            <LoginForm loginError={loginError}>
              <TitleText style={{ marginBottom: 60, fontSize: 24 }}>
                Sign in
              </TitleText>
              <FieldWrapper loginError={loginError}>
                <Field name="username" component={AnimatedInput} label="Email address" loginError={loginError} />
              </FieldWrapper>
              <PasswordWrapper loginError={loginError}>
                <Field
                  type={showPassword ? "text" : "password"}
                  name="password"
                  component={AnimatedInput}
                  label="Password"
                  loginError={loginError}
                />
                {showPassword ? (
                  <VisibilityOff
                    onClick={togglePasswordVisibility}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "69%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <Visibility
                    onClick={togglePasswordVisibility}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "69%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                  />
                )}
              </PasswordWrapper>
              <AnimatedButton
                text="Sign In"
                type="submit"
                onClick={() => handleSubmit()}
              />
              <AnimatedTitleText onClick={() => history.push("resetPassword")}>
                Forgot password?
              </AnimatedTitleText>
            </LoginForm>
          )}
        </Formik>
        <LoginIcon />
      </LoginContainer>
    </Container>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  display: flex;
  min-height: 100%;
  height: 100vh;
  background-color: #f5f6fb;
`;

const LoginContainer = styled.div<{ isMounted: boolean }>`
  align-items: center;
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 5em;
  opacity: ${({ isMounted }) => (isMounted ? 1 : 0)};
  ${({ isMounted }) =>
    isMounted &&
    css`
      animation: ${fadeIn} 0.5s ease-out;
    `}
`;

const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  25%, 75% {
    transform: translateX(-5px);
  }
  50% {
    transform: translateX(5px);
  }
`;

const LoginForm = styled.div<{ loginError: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  padding: 2em;
  width: 60%;
  height: 70%;
  border-radius: 30px;
  ${({ loginError }) =>
    loginError &&
    css`
      animation: ${errorShake} 0.3s linear;
    `}
`;

const FieldWrapper = styled.div<{ loginError: boolean }>`
  ${({ loginError }) =>
    loginError &&
    css`
      animation: ${shake} 0.3s linear;
    `}
`;

const PasswordWrapper = styled(FieldWrapper)`
  position: relative;
`;

const AnimatedButton = styled(SubmitButton)`
  width: 100%;
  align-self: flex-end;
  margin-top: 30px;
  margin-bottom: 30px;
  background-color: #01a8ce;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease;

  &:hover {
    background-color: #018ab0;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const AnimatedTitleText = styled(TitleText)`
  color: #01a8ce;
  cursor: pointer;
  transition: color 0.3s ease, transform 0.3s ease;

  &:hover {
    color: #018ab0;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const focusIn = keyframes`
  from {
    border-color: #ccc;
  }
  to {
    border-color: #01a8ce;
  }
`;

const errorShake = keyframes`
  from, to {
    border-color: #ccc;
  }
  50% {
    border-color: #ff0000;
  }
`;

const AnimatedInput = styled(Input)<{ loginError: boolean }>`
  &:focus {
    animation: ${focusIn} 0.3s forwards;
  }
  ${({ loginError }) =>
    loginError &&
    css`
      animation: ${errorShake} 2s linear;
      border-color: #ff0000;
    `}
`;
